import { useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { apiError } from '@/lib/api';
import { getPreview } from '@/lib/employeePreview';
import { Btn } from './common';

export function canEnterAdminMode(user) {
  return user?.permissions?.can_enter_admin_mode === true && !user.admin_mode
    && user.role !== 'admin' && !getPreview();
}

export default function AdminModeForm({ onBusyChange, autoFocus = false, stayHere = false }) {
  const { user, enterAdminMode } = useAuth();
  const navigate = useNavigate();
  const id = useId();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const submit = async event => {
    event.preventDefault();
    if (!password || !code.trim() || pending.current || !canEnterAdminMode(user)) return;
    pending.current = true;
    setBusy(true);
    setError('');
    onBusyChange?.(true);
    try {
      await enterAdminMode(password, code);
      toast.success('Admin-Modus aktiviert.');
      if (!stayHere) navigate('/admin/dashboard', { replace: true });
    } catch (e) {
      setError(apiError(e));
    } finally {
      setPassword('');
      setCode('');
      pending.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  };
  if (!canEnterAdminMode(user)) return null;
  return <form onSubmit={submit} className="space-y-3" aria-busy={busy}>
    <p className="text-sm text-slate-300">Bestätige den Wechsel mit deinem Passwort und einem aktuellen Authenticator-Code oder einem Wiederherstellungscode.</p>
    <label htmlFor={id} className="block text-sm text-amber-200">Passwort</label>
    <input id={id} type="password" autoComplete="current-password" autoFocus={autoFocus}
      className="ls-input w-full" value={password} disabled={busy}
      onChange={e => setPassword(e.target.value)} aria-invalid={!!error}
      aria-describedby={error ? `${id}-error` : undefined} data-testid="admin-mode-password" />
    <label htmlFor={`${id}-code`} className="block text-sm text-amber-200">Authenticator- oder Wiederherstellungscode</label>
    <input id={`${id}-code`} className="ls-input w-full" value={code} autoComplete="one-time-code" maxLength={64} required onChange={e => setCode(e.target.value)} disabled={busy} />
    {error && <p id={`${id}-error`} role="alert" className="text-sm text-red-300">{error}</p>}
    <Btn type="submit" disabled={!password || !code.trim() || busy} className="w-full" data-testid="admin-mode-enter">
      <ShieldCheck className="w-4 h-4" />{busy ? 'Passwort wird geprüft …' : 'In den Admin-Modus wechseln'}
    </Btn>
    <p className="text-xs text-slate-400">Die erhöhte Sitzung endet nach 60 Minuten.</p>
  </form>;
}
